import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '@utils/commons';
import { useErrors } from '@contexts/ErrorsContext';

interface SelectedDateType {
  day: string;
  month: string;
  year: string;
}

interface DateOfBirthProps {
  bod: string;
  selectedDate: SelectedDateType;
  setSelectedDate: React.Dispatch<React.SetStateAction<SelectedDateType>>;
}

const DateOfBirth: React.FC<DateOfBirthProps> = ({
  bod,
  selectedDate,
  setSelectedDate,
}) => {
  const [dobErrorMessage, setDobErrorMessage] = useState<string | undefined>(
    ''
  );
  const { errors } = useErrors();

  useEffect(() => {
    if (bod) {
      const [yearFromDetails, monthFromDetails, dayFromDetails] =
        bod.split('-');

      setSelectedDate({
        day: dayFromDetails || '',
        month: monthFromDetails || '',
        year: yearFromDetails || '',
      });
    }
  }, [bod, setSelectedDate]);

  const handleDateChange = (field: 'day' | 'month' | 'year', value: string) => {
    const updatedDate = { ...selectedDate, [field]: value };
    setSelectedDate(prev => updatedDate);
  };

  useEffect(() => {
    setDobErrorMessage(getErrorMessage(errors, 'dateOfBirth'));
  }, [errors]);

  return (
    <div
      className={`govuk-form-group mt-10 govuk-bp:mt-14 ${dobErrorMessage ? 'govuk-form-group--error' : ''}`}
    >
      <fieldset
        className="govuk-fieldset"
        role="group"
        aria-describedby="date-of-birth-hint"
      >
        <legend className="govuk-fieldset__legend govuk-fieldset__legend--l">
          <h1 className="govuk-fieldset__heading">Date of birth (required)</h1>
        </legend>
        <div id="date-of-birth-hint" className="govuk-hint">
          What is your date of birth? (For example, 31 3 1980)
        </div>

        <div className="govuk-date-input" id="date-of-birth">
          {dobErrorMessage && (
            <p id="date-of-birth-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>{' '}
              {dobErrorMessage}
            </p>
          )}

          <div className="govuk-date-input__item">
            <div className="govuk-form-group">
              <label
                className="govuk-label govuk-date-input__label "
                htmlFor="dateOfBirth"
              >
                Day
              </label>
              <input
                className={`govuk-input govuk-date-input__input govuk-input--width-2 ${dobErrorMessage ? 'govuk-input--error' : ''}`}
                id="dateOfBirth"
                name="date-of-birth-day"
                type="text"
                inputMode="numeric"
                maxLength={2}
                placeholder="DD"
                value={selectedDate.day}
                onChange={e => {
                  const { value } = e.target;
                  if (/^[0-9]*$/.test(value) && value.length <= 2) {
                    handleDateChange('day', value);
                  }
                }}
              />
            </div>
          </div>
          <div className="govuk-date-input__item">
            <div className="govuk-form-group">
              <label
                className="govuk-label govuk-date-input__label"
                htmlFor="date-of-birth-month"
              >
                Month
              </label>
              <input
                className={`govuk-input govuk-date-input__input govuk-input--width-2 ${dobErrorMessage ? 'govuk-input--error' : ''}`}
                id="date-of-birth-month"
                name="date-of-birth-month"
                type="text"
                inputMode="numeric"
                placeholder="MM"
                maxLength={2}
                value={selectedDate.month}
                onChange={e => {
                  const { value } = e.target;
                  if (/^[0-9]*$/.test(value) && value.length <= 2) {
                    handleDateChange('month', value);
                  }
                }}
              />
            </div>
          </div>
          <div className="govuk-date-input__item">
            <div className="govuk-form-group">
              <label
                className="govuk-label govuk-date-input__label"
                htmlFor="date-of-birth-year"
              >
                Year
              </label>
              <input
                className={`govuk-input govuk-date-input__input govuk-input--width-4 ${dobErrorMessage ? 'govuk-input--error' : ''}`}
                id="date-of-birth-year"
                name="date-of-birth-year"
                type="text"
                inputMode="numeric"
                placeholder="YYYY"
                maxLength={4}
                value={selectedDate.year}
                onChange={e => {
                  const { value } = e.target;
                  if (/^[0-9]*$/.test(value) && value.length <= 4) {
                    handleDateChange('year', value);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default DateOfBirth;
